import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';

const SearchContext = createContext();
const SearchCallbacksContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const useSearchCallbacks = () => {
  const context = useContext(SearchCallbacksContext);
  if (!context) {
    throw new Error('useSearchCallbacks must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const { trackSearchSubmitted, trackSearchCleared } = useAnalytics();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [onSearchCallback, setOnSearchCallback] = useState(null);
  const [onClearCallback, setOnClearCallback] = useState(null);

  const registerSearchCallbacks = useCallback((onSearch, onClear) => {
    setOnSearchCallback(() => onSearch);
    setOnClearCallback(() => onClear);
  }, []);

  const unregisterSearchCallbacks = useCallback(() => {
    setOnSearchCallback(null);
    setOnClearCallback(null);
    setSearchQuery('');
    setIsSearching(false);
  }, []);

  const handleSearch = useCallback(async () => {
    if (onSearchCallback) {
      trackSearchSubmitted(searchQuery);
      setIsSearching(true);
      await onSearchCallback(searchQuery);
      setIsSearching(false);
    }
  }, [onSearchCallback, searchQuery, trackSearchSubmitted]);

  const handleClear = useCallback(() => {
    trackSearchCleared();
    setSearchQuery('');
    if (onClearCallback) {
      onClearCallback();
    }
  }, [onClearCallback, trackSearchCleared]);

  const value = useMemo(() => ({
    searchQuery,
    setSearchQuery,
    isSearching,
    handleSearch,
    handleClear,
  }), [searchQuery, isSearching, handleSearch, handleClear]);

  const callbacksValue = useMemo(() => ({
    registerSearchCallbacks,
    unregisterSearchCallbacks,
  }), [registerSearchCallbacks, unregisterSearchCallbacks]);

  return (
    <SearchCallbacksContext.Provider value={callbacksValue}>
      <SearchContext.Provider value={value}>
        {children}
      </SearchContext.Provider>
    </SearchCallbacksContext.Provider>
  );
};
